import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { uniq } from "lodash";
import { useEffect, useMemo } from "react";

import {
  getMessageHashFromUrl,
  scrollToMessage,
} from "@/react/components/Chat/ChatWindowV2/helpers";
import { generateRandomId } from "@/react/helpers/generateRandomId";

import { useTipTapEditorContext } from "../TipTapEditorContext";
import { scrollToBottom } from "../utilities";
import { LinkItemCompass } from "./LinkItem/LinkItemCompass";
import type { Meta } from "./LinkItem/types";
import { MessageItem } from "./MessageItem";
import { useUnfurlLink } from "./useUnfurlLink";

let lastScrolledHashId: string | undefined;

interface LinkPreviewsProps {
  hasAttachments: boolean;
  isEditMode: boolean;
  linkMarks: any[];
  messageAttachments: any[];
  setHasLinks: (hasLinks: boolean) => void;
}

export const LinkPreviews = ({
  linkMarks,
  messageAttachments,
  hasAttachments,
  setHasLinks,
  isEditMode,
}: LinkPreviewsProps) => {
  const messageHashId = getMessageHashFromUrl();
  const {
    shouldScrollIntoView,
    chatProps: { shouldScrollToMessage },
  } = useTipTapEditorContext();
  const linkHrefs = uniq(linkMarks.map(mark => mark?.attrs?.href));
  const randomId = useMemo(() => generateRandomId(), []);

  // Normalize URL by removing trailing slash for comparison
  const normalizeUrl = (url: string) => url?.replace(/\/$/, "");
  const normalizedLinkHrefs = linkHrefs.map(normalizeUrl);

  const { filteredUnfurledLinks, isLoading } = useUnfurlLink({
    linkHrefs: normalizedLinkHrefs,
    setHasLinks,
  });

  useEffect(() => {
    const hasFilteredUnfurledLinks = Boolean(
      !isLoading && filteredUnfurledLinks.length > 0,
    );
    if (hasFilteredUnfurledLinks && shouldScrollIntoView) {
      scrollToBottom();
    } else if (
      hasFilteredUnfurledLinks &&
      shouldScrollToMessage &&
      lastScrolledHashId !== messageHashId
    ) {
      lastScrolledHashId = messageHashId;
      scrollToMessage(messageHashId, "instant");
    }
  }, [
    filteredUnfurledLinks,
    isLoading,
    shouldScrollIntoView,
    shouldScrollToMessage,
  ]);

  if (
    !(filteredUnfurledLinks.length || messageAttachments.length) ||
    isLoading
  ) {
    return null;
  }

  return (
    <>
      {hasAttachments && isEditMode && (
        <div className="border-primary mx-0.5 h-16 w-px rounded-md border-l-2" />
      )}
      <div
        className={classNames("flex w-full gap-2 pb-1", {
          "flex-wrap": !hasAttachments,
          "flex-col": !isEditMode,
        })}
      >
        {messageAttachments.map(message => (
          <MessageItem key={message.id} message={message} />
        ))}
        {filteredUnfurledLinks.map(({ url, ...meta }: any) => (
          <LinkItemCompass
            key={`${url}-${randomId}`}
            url={url}
            meta={meta as Meta}
          />
        ))}
      </div>
    </>
  );
};
